import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ControlledDatePicker from "../forms/ControlledDatePicker";
import ControlledTextField from "../forms/ControlledTextField";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CustomButtonModal from "../common/CustomBtnModal";
import { HighlightOff } from "@mui/icons-material";
import { modalStyle } from "../../_utils/modalStyle";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProfileContext } from "../../contexts/ProfileContext";
import formatTasks from "../../_utils/formatter/taskFormatter";
import useFetchToggle from "../../hooks/useFetchToggle";
import React from "react";

interface IFormInput {
  title: string;
  description: string;
  dueDate: Date | any;
}

const AddTodoModal = ({
  open,
  close,
  group,
  isFirst = false,
  groups = [],
}: any) => {
  const { t } = useTranslation("common");
  const pId = useSelector((state: any) => state.user.person_id);
  const { id } = useParams();
  const { setTasks } = useContext(ProfileContext);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(group);

  const personId = id ?? pId;

  useEffect(() => {
    setSelectedGroup(group);
  }, [group]);

  const onSubmit: SubmitHandler<IFormInput> = async (data: IFormInput) => {
    try {
      setLoading(true);
      const response = await axios.post(`tasks`, {
        ...data,
        taskGroup: `/api/task_groups/${selectedGroup?.id}`,
        finished: false,
        person: `/api/people/${personId}`,
      });

      const task = response.data;

      setTasks((groups: any) =>
        formatTasks(
          groups.map((group: any) => ({
            ...group,
            tasks:
              group.id === task.taskGroup.id
                ? [...group.tasks, task]
                : group.tasks,
          }))
        )
      );

      close();
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const dueDate = useMemo(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }, []);

  const { handleSubmit, control, reset } = useForm<IFormInput>({
    mode: "onBlur",
    defaultValues: {
      title: "",
      description: "",
      dueDate: dueDate,
    },
  });

  useEffect(() => {
    reset({ title: "", description: "", dueDate: dueDate });
  }, [isFirst, group, dueDate, open, reset]);

  const handleSelect = (task: any) => {
    console.log("TASK selected", task);
    if (!task) {
      reset({ title: "", description: "", dueDate: dueDate });
      return;
    }

    reset({
      title: task.title,
      description: task.description,
      dueDate: task.dueDate ? new Date(task.dueDate) : dueDate,
    });
  };

  return (
    <>
      <Modal open={open}>
        <Card
          sx={{
            ...modalStyle,
          }}
        >
          <Box display="flex" alignItems="center" sx={{ p: 1 }}>
            <IconButton aria-label="close" onClick={close} sx={{ ml: "auto" }}>
              <HighlightOff />
            </IconButton>
          </Box>
          <Typography textAlign="center" variant="h5" sx={{ mt: -4 }}>
            {t("todo.new_todo")}
          </Typography>
          {/* {owner === "benef" && (
            <Typography textAlign="center" variant="h5">
              {t("todo.for") + " " + person?.fullName}
            </Typography>
          )}
          {owner === "advisor" && (
            <Typography textAlign="center" variant="h5">
              {t("todo.for") + " " + t("roles.advisor")}
            </Typography>
          )} */}

          <CardContent>
            {isFirst && Array.isArray(groups) && groups.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="select-group-label">
                  {t("todo.group")}
                </InputLabel>
                <Select
                  labelId="select-group-label"
                  value={selectedGroup?.id ?? ""}
                  label={t("todo.group")}
                  onChange={(e) => {
                    const found = groups.find(
                      (g: any) => g.id === e.target.value
                    );
                    setSelectedGroup(found);
                  }}
                >
                  {groups.map((g: any) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <DefaultSelector
              groupId={selectedGroup?.id}
              onSelect={handleSelect}
            />
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2}>
                <ControlledTextField
                  label={t(`todo.todo_name`) + " *"}
                  control={control}
                  name="title"
                  required
                />

                <Box sx={{ mt: 2 }}>
                  <ControlledTextField
                    label={t(`todo.todo_description`)}
                    control={control}
                    name="description"
                    multiline
                  />
                </Box>
                <Box display="flex" flexWrap="wrap" sx={{ mb: 4 }}>
                  <ControlledDatePicker
                    control={control}
                    name="dueDate"
                    label={t("todo.limit_date")}
                  />
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  justifyContent="center"
                  sx={{ pt: 2 }}
                >
                  <CustomButtonModal
                    secondary
                    type="button"
                    onClick={close}
                    variant="outlined"
                    color="secondary"
                    text={t(`btn.cancel`)}
                  />
                  <CustomButtonModal
                    type="submit"
                    variant="contained"
                    text={t("btn.valid")}
                    disabled={loading}
                  />
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Modal>
    </>
  );
};

export default AddTodoModal;

const DefaultSelector: React.FC<any> = ({ groupId, onSelect }) => {
  const { t } = useTranslation("common");

  const { loading, data } = useFetchToggle({
    api: `task_groups/${groupId}/default_tasks`,
    canFetch: groupId,
  });

  const defaultTasks = useMemo(() => {
    if (!data) return [];
    return data?.["hydra:member"]?.map((task: any) => ({
      id: task.id,
      title: task.title,
      dueDate: task.dueDate,
      description: task.description,
    }));
  }, [data]);

  const [taskId, setTaskId] = React.useState("");

  const handleChange = (event: any) => {
    setTaskId(event.target.value);
    onSelect(defaultTasks.find((task: any) => task.id === event.target.value));
  };

  if (loading || defaultTasks.length === 0) return null;

  return (
    <FormControl variant="standard" sx={{ mb: 2, width: "100%" }}>
      <InputLabel id="demo-simple-select-standard-label">
        {t("todo.label")}
      </InputLabel>
      <Select
        labelId="demo-simple-select-standard-label"
        id="demo-simple-select-standard"
        value={taskId}
        onChange={handleChange}
        label={t("todo.label")}
        disabled={loading}
      >
        <MenuItem value="">
          <em>{t("none_f")}</em>
        </MenuItem>
        {defaultTasks.map((task: any) => (
          <MenuItem key={task.id} value={task.id}>
            <div>
              <Typography>{task.title}</Typography>
              {/* <Typography variant="caption">{task.description}</Typography> */}
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
